/**
 * מקורות נתונים — the connections, their state, and how old their data is. 07 §2.3, §6.2, N-5, DG-2.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * "לא הוגדר" IS NOT A KIND OF "תקלה", AND "נמשך היום" IS NOT "עדכני".
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Two collapses this screen refuses, both of them measured rather than imagined.
 *
 * ONE — NEVER CONFIGURED vs BROKEN. 07 N-5: a missing `data_source_type` silently became a working
 * default on 5 of 313 companies, in two independent code paths. Those five behaved exactly like five
 * configured companies, and nothing on any screen said otherwise. So `לא הוגדר` has its own chip,
 * its own sentence, its own next step — and its freshness line says *"לא התקבלו נתונים מהחיבור הזה
 * מעולם"* rather than showing a date, because there is no date and inventing one is the defect.
 *
 * TWO — THE TWO WATERMARKS. 07 §6.2 keeps `dataAsOfUtc` (the latest business date actually present)
 * apart from `fetchedAtUtc` (when we last successfully talked to the source), because *"the file
 * mtime moves on any rewrite, including a zero-row pull — the mechanism that let the June 2026
 * canonical freeze stay invisible."* A successful pull that returns nothing advances the fetch time
 * and MUST NOT advance the data time. The headline on every row is therefore the DATA watermark, and
 * the fetch time is shown beneath it, labelled as what it is. `con_fixture_0003` in the fixtures is
 * exactly that shape: fetched this morning, data three months old, and the row says so.
 *
 * THREE — AND `UNKNOWN` IS NOT `FRESH`. With no schedule and no SLA there is no basis for a verdict,
 * and the state is `UNKNOWN`. An unmeasurable SLA reported as healthy is a state that existed and
 * reached nobody.
 *
 * ── ORIGIN IS ON EVERY ROW ──────────────────────────────────────────────────────────────────────
 *
 * `live_source` vs `development_fixture` is the ONE branch a module is invited to make, and it is
 * the specific defence against a demo surface being mistaken for a proven integration. Every row
 * carries it, so a screen backed by a fake adapter says so per connection — not only through the
 * ribbon at the top of the page.
 *
 * ── AND NO HOST APPEARS ANYWHERE ────────────────────────────────────────────────────────────────
 *
 * 07 §2.3: an endpoint reference is a LOGICAL name resolved from deployment configuration — *"not a
 * connection string, not a host literal"* — and 07 N-6 forbids an adapter to hold a credential value
 * at all. There is no column here that could carry either, and the missing-configuration list names
 * the FIELDS that are absent, never their values.
 */

import { InfoHint } from '../components/InfoHint';
import { useCallback, useState } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { InfoNote } from '../components/InfoNote';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import type { SourceConnectionSummary } from '../api/contract';
import { connectorStatusHe } from '../api/adminVocabulary';
import {
  completenessTextFor,
  freshnessTextFor,
  missingConfigurationHe,
  sourceStatusTextFor,
} from '../api/sourceStatusText';
import { routes } from '../routes';

/**
 * THE ACCOUNTING SYSTEMS AN OFFICE MAY NAME WHEN IT DEFINES A CONNECTION.
 *
 * **THIS LIST IS THE ONE THE OWNER ASKED ABOUT**, 2026-10-29 item 10: *"בדוק היכן הרשימה
 * מוגדרת. אם זו רשימת תצוגה בלבד, תקן אותה בלי להוסיף מיגרציה מיותרת."* It is display only, and
 * dropping `finsite` from it needs no migration. **The per-company accounting-system picker is a
 * different list and is NOT display only** -- it reads `system_role = 'accounting'` out of the
 * catalogue, which is the database record 0023 corrects.
 *
 * `core.external_system.system_code`, which 0005 seeds and `system_role` classifies. These are
 * EXACTLY the codes classified `accounting` once every migration has been applied -- 0020 marked
 * five, and **0023 sets `finsite` to NULL**, which 0020 declared as a real member for exactly this
 * state: *"an unclassified row is invisible to [the picker] -- fail-closed."* NOT `banking`, on the
 * Owner's explicit correction: *"אל תעביר אותה אוטומטית לרשימת חיבורים בנקאיים כאילו כבר בחרנו
 * ספק."* So it is offered in NO list, and the provider decision stays open.
 *
 * `sourceSystems.test.ts` derives the expected set BY APPLYING THE MIGRATIONS IN ORDER, so a sixth
 * system added tomorrow, or a later reclassification, fails that test here rather than quietly
 * leaving this list one row behind the estate.
 *
 * THE FIRST DRAFT OF THIS LIST WAS WRONG IN TWO OF FIVE, and it is worth recording: it carried
 * `sap_b1`, which the seed spells `sap`, and one further vendor code that is not in the catalogue
 * at all. **Neither would have been refused.** The claim that used to stand here -- that the server
 * refuses an unknown code -- was not true of the product: `createConnection` checked only that the
 * field was non-blank, `core.source_connection.source_system_code` has no foreign key, and the code
 * surfaced much later as `adapter_not_registered` at probe time. That gap is closed in the same
 * commit as this list: `tenant.source.create` now reads the catalogue and refuses a code that is
 * not an active accounting system. SO THIS LIST IS A CONVENIENCE AND THE SERVER IS THE AUTHORITY --
 * which is what it already said, and now what it does.
 *
 * They are typed here rather than read from a route because the catalogue endpoint that lists
 * accounting systems is scoped to ONE COMPANY (`companies.source_system.read`), and a connection
 * belongs to the office rather than to a company.
 */
export const SELECTABLE_SYSTEMS: readonly { readonly code: string; readonly label: TextKey }[] = [
  { code: 'priority', label: 'system.priority' },
  { code: 'hashavshevet', label: 'system.hashavshevet' },
  { code: 'rivhit', label: 'system.rivhit' },
  { code: 'sap', label: 'system.sap' },
];

type CreateOutcome =
  | { readonly kind: 'created'; readonly missing: readonly string[] }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function SourceConnectionsScreen(): JSX.Element {
  const { t } = useLocale();

  const ctx = useWorkContext();
  /*
   * `null` = THE OFFICE HAS NOT TYPED A NAME, and the field then shows one suggested from the
   * system they picked. The Owner: *"אין לחייב משתמש להמציא שם לחיבור יחיד"*.
   *
   * It is `null` rather than `''` for the reason it is everywhere else in this round: the two are
   * different facts, and `''` cannot tell "nothing typed yet" from "cleared on purpose".
   */
  const [typedName, setTypedName] = useState<string | null>(null);
  const [systemCode, setSystemCode] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<CreateOutcome | null>(null);

  /*
   * THE READ THAT REPLACED THE FIXTURE. `useCallback` because `useServerData` has the reader in
   * its effect's dependency list: a new function identity on every render is a fetch on every
   * render.
   */
  const read = useCallback(() => callOperation('tenant.source.list', {}), []);
  const connectionsRead = useServerData(read);

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('nav.sources')} requires="TENANT" actual={ctx.surface} />;
  }

  const back = { labelHe: t('settings.title'), href: routes.tenantAdmin };

  /**
   * The name this connection gets if nobody types one: the system's own display name.
   *
   * It is computed and not stored, so changing the system before saving changes the suggestion --
   * which is what somebody correcting a mis-click expects -- and typing anything at all stops it.
   */
  const suggestedName =
    systemCode === ''
      ? ''
      : (SELECTABLE_SYSTEMS.find((system) => system.code === systemCode)?.label === undefined ? systemCode : t(SELECTABLE_SYSTEMS.find((system) => system.code === systemCode)!.label));
  const nameInForm = typedName ?? suggestedName;

  function create(e: FormEvent): void {
    e.preventDefault();
    setSending(true);
    setOutcome(null);
    void callOperation('tenant.source.create', { body: { displayNameHe: nameInForm, sourceSystemCode: systemCode } })
      .then(
        (answer) => {
          setOutcome({ kind: 'created', missing: answer.missingConfiguration });
          setTypedName(null);
          setSystemCode('');
          connectionsRead.reload();
        },
        (error: unknown) => setOutcome({ kind: 'failed', failure: classifyActionError(error) }),
      )
      .finally(() => setSending(false));
  }

  return (
    <AppShell
      screenId="tenant-sources"
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('nav.sources')}
      backToParent={back}
    >
      {/*
        WHAT DEFINING A CONNECTION DOES, AND WHAT IT DOES NOT. It is stated in the open rather than
        behind the marker, because somebody is about to type a credential reference on the strength
        of it: the definition is stored and audited; nothing is fetched, because this build carries
        no connector software for any accounting system.
      */}
      <form className="form" onSubmit={create}>
        <div className="form__head">
          <h2 className="form__title">{t('source.newTitle')}</h2>
          {/*
            THE NOTICE BECAME AN ⓘ, AND IT WAS KEPT RATHER THAN DELETED. **Owner, 2026-10-28:**
            *"אם ממש חשוב שיהיה הסבר תוסיף סימן של i."* This is one of the places where it is: his
            own standing rule is *"אין להציג רכיב כפעיל כשהוא אינו מחובר"*, and somebody is about
            to name a credential on the strength of what this form appears to do. Saving a
            definition is not a connection, and this build carries no connector software — which
            the screen cannot show, because there is nothing there to show.
          */}
          <InfoHint aboutHe={t('source.newTitle')}>
            {t('source.whatItDoesBody')}
            {t('source.probeSaysSo')}
          </InfoHint>
          <p className="form__head-summary">
            {systemCode === ''
              ? t('source.noSystemYet')
              : t('source.createSummary')}
          </p>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={sending || nameInForm.trim() === '' || systemCode === ''}
          >
            {sending ? t('common.saving') : t('source.create')}
          </button>
        </div>

        <label className="field">
          <span className="field__label">{t('source.name')}</span>
          <input
            className="field__input"
            name="displayNameHe"
            value={nameInForm}
            onChange={(e) => setTypedName(e.currentTarget.value)}
          />
          {/* 07 section 2.3: never a host and never a connection string. */}
          <span className="field__hint">
            {t('source.nameHint')}
          </span>
        </label>

        <fieldset className="field field--choice">
          <legend className="field__label">{t('source.system')}</legend>
          {SELECTABLE_SYSTEMS.map((system) => (
            <label key={system.code} className="choice">
              <input
                type="radio"
                name="sourceSystemCode"
                value={system.code}
                checked={systemCode === system.code}
                onChange={() => setSystemCode(system.code)}
              />
              <span>{t(system.label)}</span>
            </label>
          ))}
          <span className="field__hint">
            {t('source.systemHint')}
          </span>
        </fieldset>
        <p className="form__note">
          {t('source.createDoesNotConfigure')}
        </p>
      </form>

      {outcome === null ? null : outcome.kind === 'created' ? (
        <StateBlock
          titleHe={t('source.createdTitle')}
          /*
           * THIS LINE PRINTED `endpointRef, secretRef` TO THE OFFICE. **Section 4, 2026-10-26:**
           * *"אין להציג למשתמש שדות פנימיים כמו `endpointRef` ו־`secretRef`"*.
           *
           * `outcome.missing` is the SERVER's field list and arrives as wire names. `join(', ')`
           * put them on screen unchanged -- the one path in this round where the Owner could
           * actually have seen them, since the form's own labels were Hebrew from the start.
           *
           * `missingConfigurationHe` names the ones this build has a word for and COUNTS the rest,
           * so a field added on the server before this browser knows its label reads as
           * *"ועוד 1 פרטים"* rather than as an identifier nobody outside the code can act on.
           */
          bodyHe={
            outcome.missing.length === 0
              ? t('source.createdBody')
              : `${t('source.createdBody')} ${missingConfigurationHe(outcome.missing)}`
          }
        />
      ) : (
        <ActionFailureNotice failure={outcome.failure} />
      )}

      <ServerRead result={connectionsRead.result} whatHe={t('source.reading')}>
        {(data) => <ConnectionList connections={data.connections} />}
      </ServerRead>
    </AppShell>
  );
}

/**
 * The list, given rows that ARRIVED. A list cannot say whether it is empty because the office has
 * connected nothing or because the read was refused, which is why `<ServerRead>` wraps it and the
 * genuine zero-state lives in here.
 */
function ConnectionList({
  connections,
}: {
  readonly connections: readonly SourceConnectionSummary[];
}): JSX.Element {
  const { t } = useLocale();

  if (connections.length === 0) {
    return (
      <StateBlock
        titleHe={t('source.emptyTitle')}
        bodyHe={t('source.emptyBody')}
        tone="empty"
        nextStep={<p>{t('source.emptyNext')}</p>}
      />
    );
  }

  const neverConfigured = connections.filter((c) => c.state === 'NOT_CONFIGURED').length;
  const failing = connections.filter((c) => c.state === 'ERROR').length;

  return (
    <>
      {/* 09 §5.1's rule about figures: the headline says which N it is, in words. */}
      <p className="summary-line">
        {`${t('source.summaryConnections')}: ${String(connections.length)} · ${t('source.summaryUnconfigured')}: ${String(neverConfigured)} · ${t('source.summaryFailing')}: ${String(failing)}`}
      </p>

      <section className="connections">
        {connections.map((connection) => {
          const status = sourceStatusTextFor(connection);
          const freshness = freshnessTextFor(connection.freshness);
          const completeness = completenessTextFor(connection.completeness);
          const connector = connectorStatusHe(connection.connectorStatus);
          const missing = missingConfigurationHe(connection.missingConfiguration);

          return (
            <article
              key={connection.connectionUid}
              className="connection"
              data-state={connection.state}
            >
              <header className="connection__head">
                <h2 className="connection__title">{connection.displayNameHe}</h2>
                <StatusChip labelHe={status.labelHe} tone={status.tone} />
                <StatusChip labelHe={connector.labelHe} tone={connector.tone} />
                {connection.origin === 'development_fixture' ? (
                  // Per connection, not only in the page ribbon. A single fake row in a list of real
                  // ones is the case the ribbon cannot describe.
                  <StatusChip labelHe={t('source.devOnly')} tone="warn" />
                ) : null}
              </header>

              <p className="connection__explanation">{status.explanationHe}</p>

              {missing === '' ? null : (
                // EVERY absent field, not the first. An operator fixing one at a time runs the probe
                // five times (07 N-5).
                <p className="connection__missing">{missing}</p>
              )}

              <dl className="connection__facts">
                <div className="connection__fact">
                  <dt>{t('source.freshness')}</dt>
                  <dd>
                    <StatusChip labelHe={freshness.labelHe} tone={freshness.tone} />
                    {/* The DATA watermark. This is the sentence the office reads. */}
                    <span className="connection__as-of">{freshness.asOfHe}</span>
                    {freshness.fetchedHe === null ? null : (
                      // The FETCH watermark, labelled, beneath. Never the headline.
                      <span className="connection__fetched">{freshness.fetchedHe}</span>
                    )}
                  </dd>
                </div>

                <div className="connection__fact">
                  <dt>{t('source.completeness')}</dt>
                  <dd>
                    <StatusChip labelHe={completeness.labelHe} tone={completeness.tone} />
                    <span className="connection__as-of">{completeness.explanationHe}</span>
                    {completeness.totalRequiresQualification ? (
                      // 07 DG-2: a total is never rendered over an incomplete set without saying so.
                      <span className="connection__qualified">
                        {t('source.figuresQualified')}
                      </span>
                    ) : null}
                  </dd>
                </div>

                <div className="connection__fact">
                  <dt>{t('source.linkedCompanies')}</dt>
                  <dd>
                    <span dir="ltr">{connection.boundCompanyCount}</span>
                  </dd>
                </div>
              </dl>

              <p className="connection__next-step">{status.nextStepHe}</p>

              {/*
                THE NEXT STEP IS NOW REACHABLE, WHICH IT WAS NOT.
                Every sentence above ends in an instruction — "השלמת פרטי החיבור", "הרצת בדיקת חיבור"
                — and until this link existed there was nowhere to carry any of them out. A written
                next step with no address is the G-03 pattern in one line: correct, documented, and
                reaching nothing.
              */}
              <div className="actions-row">
                <a className="btn" href={routes.tenantSourceConnection(connection.connectionUid)}>
                  {t('source.openConnection')}
                </a>
              </div>
            </article>
          );
        })}
      </section>
    </>
  );
}
